import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { UpdateAllowedDomainRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateAllowedDomainRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useUpdateAllowedDomainRPCClientImpl } from "../rpcs/RPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import { BoundedName } from "@pulse/pulse-rpcs/lib/types/boundedName";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";
import { AllowedDomainDetailsStore } from "./AllowedDomainDetailsStore";

type UpdateAllowedDomainRPCError = Exclude<
  UpdateAllowedDomainRPC.RPCError,
  | UpdateAllowedDomainRPC.RPCError.DomainAlreadyExists
  | UpdateAllowedDomainRPC.RPCError.DomainNameAlreadyExists
>;

const getInitialState = (): SnapshotIn<typeof UpdateAllowedDomainStore> => ({
  domainId: EMPTY_CHARACTER,
  domainName: createBoundedNameModel(),
  domain: createBoundedNameModel(),
  loadingUpdateAllowedDomain: false,
  updateAllowedDomainRPCError: null,
});

export const UpdateAllowedDomainStore = types
  .model("UpdateAllowedDomainStore", {
    domainId: types.string,
    domainName: BoundedNameModel,
    domain: BoundedNameModel,
    loadingUpdateAllowedDomain: types.boolean,
    updateAllowedDomainRPCError: types.maybeNull(
      types.enumeration(
        "UpdateAllowedDomainRPCError",
        Object.values(UpdateAllowedDomainRPC.RPCError).filter(
          (error): error is UpdateAllowedDomainRPCError =>
            error !== UpdateAllowedDomainRPC.RPCError.DomainAlreadyExists &&
            error !== UpdateAllowedDomainRPC.RPCError.DomainNameAlreadyExists,
        ),
      ),
    ),
  })
  .views((store) => ({
    get disableSubmit(): boolean {
      const allowedDomainDetailsStore = (
        getRoot(store) as {
          allowedDomainDetailsStore: Instance<typeof AllowedDomainDetailsStore>;
        }
      ).allowedDomainDetailsStore;

      const isEmptyData = !store.domainName.name || !store.domain.name;
      const hasAnyChanges =
        store.domainName.name === allowedDomainDetailsStore.domainName.name &&
        store.domain.name === allowedDomainDetailsStore.domain.name;
      const hasValidationError =
        store.domainName.validationError ?? store.domain.validationError;

      return !!(isEmptyData || hasAnyChanges || hasValidationError);
    },
  }))
  .actions((store) => ({
    setDomainId: (domainId: string): void => {
      store.domainId = domainId;
    },
    updateAllowedDomain: flow(function* () {
      try {
        store.loadingUpdateAllowedDomain = true;
        store.updateAllowedDomainRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new UpdateAllowedDomainRPC.Request(
          new LeoUUID(store.domainId),
          new BoundedName(store.domainName.name),
          new BoundedName(store.domain.name),
        );

        const result: LeoRPCResult<
          UpdateAllowedDomainRPC.Response,
          UpdateAllowedDomainRPC.Errors.Errors
        > =
          yield useUpdateAllowedDomainRPCClientImpl(apiClient).execute(request);

        if ("error" in result) {
          switch (result.error.code) {
            case UpdateAllowedDomainRPC.RPCError.DomainAlreadyExists:
              store.domain.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            case UpdateAllowedDomainRPC.RPCError.DomainNameAlreadyExists:
              store.domainName.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            case UpdateAllowedDomainRPC.RPCError.DomainNotFound:
              store.updateAllowedDomainRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingUpdateAllowedDomain = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createUpdateAllowedDomainStore = (): Instance<
  typeof UpdateAllowedDomainStore
> => UpdateAllowedDomainStore.create(getInitialState());
